import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchParkings, addParking, updateParking, deleteParking } from '../../store/parkingSlice';

const AMENITIES_LIST = ['CCTV', 'Security Guard', 'Covered Parking', 'EV Charging', 'Handicap Access', 'Valet', '24/7 Open', 'Restrooms', 'Car Wash'];
const VEHICLE_TYPES = ['Car', 'Bike', 'SUV', 'Bus', 'Truck'];

const emptyForm = {
  name: '', address: '', description: '',
  pricePerHour: '', pricePerDay: '',
  totalSlots: '', vehicleTypes: ['Car'],
  amenities: [], is24Hours: true,
  openTime: '08:00', closeTime: '22:00',
  lat: '19.8762', lng: '75.3433',
};

export default function AdminParkings() {
  const dispatch = useDispatch();
  const { parkings, loading } = useSelector(s => s.parking);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchParkings());
  }, [dispatch]);

  const filtered = parkings.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.address.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setShowModal(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, address: p.address, description: p.description || '',
      pricePerHour: p.pricePerHour, pricePerDay: p.pricePerDay || '',
      totalSlots: p.totalSlots, vehicleTypes: p.vehicleTypes || ['Car'],
      amenities: p.amenities || [], is24Hours: p.is24Hours,
      openTime: p.openTime || '08:00', closeTime: p.closeTime || '22:00',
      lat: p.location?.coordinates?.[1] || '19.8762',
      lng: p.location?.coordinates?.[0] || '75.3433',
    });
    setEditingId(p._id);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...form,
      pricePerHour: Number(form.pricePerHour),
      pricePerDay: form.pricePerDay ? Number(form.pricePerDay) : undefined,
      totalSlots: Number(form.totalSlots),
      location: { type: 'Point', coordinates: [parseFloat(form.lng), parseFloat(form.lat)] },
    };
    if (editingId) {
      dispatch(updateParking({ id: editingId, data }));
    } else {
      dispatch(addParking(data));
    }
    setShowModal(false);
  };

  const handleDelete = (id, name) => {
    if (window.confirm(`Delete "${name}"? This cannot be undone.`)) {
      dispatch(deleteParking(id));
    }
  };

  const toggleAmenity = (a) => {
    setForm(f => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a],
    }));
  };

  const toggleVehicle = (v) => {
    setForm(f => ({
      ...f,
      vehicleTypes: f.vehicleTypes.includes(v) ? f.vehicleTypes.filter(x => x !== v) : [...f.vehicleTypes, v],
    }));
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none',
  };

  return (
    <div style={{ paddingTop: '64px', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <Link to="/admin" style={{ color: '#6366f1', fontSize: '13px', textDecoration: 'none' }}>← Admin</Link>
              <span style={{ color: '#374151' }}>/</span>
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>Parkings</span>
            </div>
            <h1 style={{ fontSize: '26px', fontWeight: '900' }}>Manage Parkings</h1>
          </div>
          <button onClick={openAdd} style={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff', border: 'none', borderRadius: '12px',
            padding: '12px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
          }}>
            + Add Parking
          </button>
        </div>

        {/* Search */}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search parkings..."
          style={{ ...inputStyle, marginBottom: '20px', maxWidth: '400px' }}
        />

        {/* Table */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                  {['Name', 'Address', 'Slots', 'Price/hr', 'Available', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', fontSize: '12px', fontWeight: '700', borderBottom: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No parkings found</td></tr>
                ) : (
                  filtered.map((p) => {
                    const availPct = p.totalSlots > 0 ? (p.availableSlots / p.totalSlots) * 100 : 0;
                    const statusColor = availPct > 50 ? '#10b981' : availPct > 20 ? '#f59e0b' : '#ef4444';
                    return (
                      <tr key={p._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.2s' }}>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '2px' }}>{p.name}</div>
                          {p.isVerified && <span style={{ fontSize: '10px', color: '#6366f1', background: 'rgba(99,102,241,0.1)', borderRadius: '4px', padding: '1px 6px' }}>✓ Verified</span>}
                        </td>
                        <td style={{ padding: '14px 16px', color: '#94a3b8', fontSize: '13px', maxWidth: '200px' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.address}</div>
                        </td>
                        <td style={{ padding: '14px 16px', fontWeight: '700', fontSize: '14px' }}>{p.totalSlots}</td>
                        <td style={{ padding: '14px 16px', color: '#6366f1', fontWeight: '800', fontSize: '15px' }}>₹{p.pricePerHour}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden', minWidth: '60px' }}>
                              <div style={{ height: '100%', width: `${availPct}%`, background: statusColor, borderRadius: '3px' }} />
                            </div>
                            <span style={{ fontSize: '12px', color: statusColor, fontWeight: '700', whiteSpace: 'nowrap' }}>{p.availableSlots}/{p.totalSlots}</span>
                          </div>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            fontSize: '11px', fontWeight: '700',
                            color: p.isActive ? '#10b981' : '#ef4444',
                            background: p.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                            borderRadius: '6px', padding: '3px 8px',
                          }}>
                            {p.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => openEdit(p)} style={{
                              padding: '6px 14px', background: 'rgba(99,102,241,0.1)',
                              border: '1px solid rgba(99,102,241,0.3)', borderRadius: '8px',
                              color: '#6366f1', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            }}>Edit</button>
                            <button onClick={() => handleDelete(p._id, p.name)} style={{
                              padding: '6px 14px', background: 'rgba(239,68,68,0.1)',
                              border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px',
                              color: '#ef4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                            }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2000, padding: '20px',
            }}
            onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: '#1a1a2e', border: '1px solid rgba(99,102,241,0.3)',
                borderRadius: '24px', padding: '32px', width: '100%', maxWidth: '600px',
                maxHeight: '90vh', overflowY: 'auto',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '800' }}>{editingId ? 'Edit Parking' : 'Add New Parking'}</h2>
                <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer' }}>✕</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Parking Name *</label>
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Gulmandi Parking" style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Address *</label>
                    <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required placeholder="Full address" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Price/Hour (₹) *</label>
                    <input type="number" value={form.pricePerHour} onChange={e => setForm({ ...form, pricePerHour: e.target.value })} required min="1" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Total Slots *</label>
                    <input type="number" value={form.totalSlots} onChange={e => setForm({ ...form, totalSlots: e.target.value })} required min="1" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Latitude</label>
                    <input value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} placeholder="19.8762" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Longitude</label>
                    <input value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} placeholder="75.3433" style={inputStyle} />
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>Vehicle Types</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {VEHICLE_TYPES.map(v => (
                      <button key={v} type="button" onClick={() => toggleVehicle(v)} style={{
                        padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                        background: form.vehicleTypes.includes(v) ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${form.vehicleTypes.includes(v) ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)'}`,
                        color: form.vehicleTypes.includes(v) ? '#6366f1' : '#94a3b8',
                      }}>{v}</button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>Amenities</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {AMENITIES_LIST.map(a => (
                      <button key={a} type="button" onClick={() => toggleAmenity(a)} style={{
                        padding: '5px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', fontWeight: '600',
                        background: form.amenities.includes(a) ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${form.amenities.includes(a) ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.1)'}`,
                        color: form.amenities.includes(a) ? '#10b981' : '#94a3b8',
                      }}>{a}</button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <input type="checkbox" id="is24h" checked={form.is24Hours} onChange={e => setForm({ ...form, is24Hours: e.target.checked })} style={{ width: 16, height: 16, accentColor: '#6366f1' }} />
                  <label htmlFor="is24h" style={{ fontSize: '13px', color: '#94a3b8', cursor: 'pointer' }}>Open 24 Hours</label>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{
                    flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
                    color: '#94a3b8', fontSize: '14px', cursor: 'pointer',
                  }}>Cancel</button>
                  <button type="submit" style={{
                    flex: 2, padding: '12px',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    border: 'none', borderRadius: '12px',
                    color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                  }}>
                    {editingId ? 'Update Parking' : 'Add Parking'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}